import { useDepsChanged } from './chunk-I37XHMWY.js';
import { notifyError } from './chunk-26PMNFER.js';
import * as React2 from 'react';
import { jsx } from 'react/jsx-runtime';
import { thrownToErrorEnvelope, errorEnvelopeToErrorBody } from '@authe/fe-commons/api';
import { applyServerErrors } from '@authe/fe-commons/forms';

var ApiClientContext = React2.createContext(null);
function ApiProvider({ client, children }) {
  return /* @__PURE__ */ jsx(ApiClientContext.Provider, { value: client, children });
}
function useApiClient() {
  const client = React2.useContext(ApiClientContext);
  if (!client) throw new Error("useApiClient must be used inside <ApiProvider>");
  return client;
}
function useOptionalApiClient() {
  return React2.useContext(ApiClientContext);
}
function queryData(data) {
  return { statusCode: 200, ok: true, data };
}
function emptyState(enabled) {
  return {
    data: void 0,
    envelope: null,
    error: null,
    isLoading: enabled,
    isFetching: false,
    isPlaceholderData: false
  };
}
function useQuery(fetcher, options = {}) {
  const client = useApiClient();
  const enabled = options.enabled !== false;
  const keepPreviousData = options.keepPreviousData === true;
  const fetcherRef = React2.useRef(fetcher);
  fetcherRef.current = fetcher;
  const onSuccessRef = React2.useRef(options.onSuccess);
  onSuccessRef.current = options.onSuccess;
  const onErrorRef = React2.useRef(options.onError);
  onErrorRef.current = options.onError;
  const [state, setState] = React2.useState(() => emptyState(enabled));
  const [tick, setTick] = React2.useState(0);
  const refetch = React2.useCallback(() => setTick((t) => t + 1), []);
  const extraDeps = options.deps ?? [];
  const priorClient = React2.useRef(client);
  const clientChanged = priorClient.current !== client;
  priorClient.current = client;
  if (useDepsChanged([client, ...extraDeps])) {
    setState(
      (s) => !clientChanged && keepPreviousData && s.data !== void 0 ? { ...s, error: null, isLoading: false, isFetching: enabled, isPlaceholderData: true } : { ...emptyState(enabled), isFetching: enabled }
    );
  }
  React2.useEffect(() => {
    if (!enabled) return;
    let active = true;
    const ctrl = new AbortController();
    setState((s) => ({ ...s, isFetching: true, isLoading: s.data === void 0, error: null }));
    fetcherRef.current(client, ctrl.signal).then(
      (res) => {
        if (!active) return;
        if (res.ok) {
          setState({
            data: res.data,
            envelope: res,
            error: null,
            isLoading: false,
            isFetching: false,
            isPlaceholderData: false
          });
          onSuccessRef.current?.(res.data, res);
        } else {
          setState((s) => ({ ...s, error: res, isLoading: false, isFetching: false }));
          onErrorRef.current?.(res);
        }
      },
      (err) => {
        if (!active) return;
        if (ctrl.signal.aborted) {
          setState((s) => ({ ...s, isFetching: false }));
          return;
        }
        const envelope = thrownToErrorEnvelope(err);
        setState((s) => ({ ...s, error: envelope, isLoading: false, isFetching: false }));
        onErrorRef.current?.(envelope);
      }
    );
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [client, enabled, tick, ...extraDeps]);
  return { ...state, refetch };
}
function useMutation(fn, options = {}) {
  const client = useApiClient();
  const optionsRef = React2.useRef(options);
  optionsRef.current = options;
  const [data, setData] = React2.useState(void 0);
  const [error, setError] = React2.useState(null);
  const [isLoading, setIsLoading] = React2.useState(false);
  const reset = React2.useCallback(() => {
    setData(void 0);
    setError(null);
    setIsLoading(false);
  }, []);
  const mutate = React2.useCallback(
    async (args) => {
      const opts = optionsRef.current;
      setIsLoading(true);
      setError(null);
      let res;
      try {
        res = await fn(client, args);
      } catch (err) {
        res = thrownToErrorEnvelope(err);
      }
      if (res.ok) {
        setData(res.data);
        setError(null);
        opts.onSuccess?.(res.data, res, args);
      } else {
        setError(res);
        opts.onError?.(res, args);
        if (opts.setFormError) applyServerErrors(errorEnvelopeToErrorBody(res), opts.setFormError);
        if (opts.notifyOnError) {
          notifyError(res, typeof opts.notifyOnError === "object" ? opts.notifyOnError : {});
        }
      }
      setIsLoading(false);
      return res;
    },
    [client, fn]
  );
  return { mutate, data, error, isLoading, reset };
}

export { ApiProvider, queryData, useApiClient, useMutation, useOptionalApiClient, useQuery };
//# sourceMappingURL=chunk-L4ULLXZZ.js.map
//# sourceMappingURL=chunk-L4ULLXZZ.js.map